import { KeyboardArrowDown, KeyboardArrowUp, Check } from "@mui/icons-material"
import {
  TableCell,
  TableRow,
  Typography,
  Skeleton,
  Button,
  Collapse,
  Box,
  Stack,
} from "@mui/material"

import { useQuery } from "@tanstack/react-query"
import { memo, useMemo, useState } from "react"

import { OrderAgent, OrderType, readAgentInfo } from "@/api/agents-api"
import { getAgentTxns, getLastTransactions } from "@/api/messages-api"
import { useTokenPrices } from "@/api/portfolio-api"
import { getBalance } from "@/api/token-api"
import { IdBlock } from "@/components/IdBlock"
import { NumberFont } from "@/components/RootLayout/fonts"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"
import { useTokenInfo } from "@/hooks/use-token-info"
import { PRICE_DECIMALS } from "@/settings"
import { truncateId } from "@/utils/data-utils"
import {
  formatFullDate,
  formatNumberAuto,
  formatRelative,
  parseBigIntAsNumber,
} from "@/utils/format"

const TITLE_MAP: Record<OrderType, string> = {
  "dca-order": "DCA Order",
  "limit-order": "Limit Order",
  "stop-order": "Stop Loss Order",
}

type StopLossVariant = "market" | "limit" | "trailing"

type OrderRowProps = {
  order: OrderAgent
  onAgentValue?: (value: number) => void
  singleAgentView?: boolean
  index?: number
}

function OrderRowBase(props: OrderRowProps) {
  const { order, onAgentValue, singleAgentView = false, index = 0 } = props

  const [showDetails, setShowDetails] = useState(false)

  const { data: agentInfo, refetch: fetchAgentInfo } = useQuery({
    queryKey: ["agent_info", order.id],
    queryFn: async () => {
      // console.log("Fetching Agent Info", agent.id)
      const res = await readAgentInfo(order.id)
      console.log("onBotegaAgentInfo", order.id, res)
      return res
    },
    enabled: !!order.id,
  })

  let filledPrice,
    filledPercentage,
    output,
    stopTriggerPrice,
    stopLimitPrice,
    stopVariant: StopLossVariant | undefined,
    trailingDiff,
    triggerPriceHit

  if (order.type === "limit-order") {
    filledPrice = agentInfo?.Strategy?.Primitives?.["main-limit-order"]?.["Filled-Price"] || null
    filledPercentage =
      agentInfo?.Strategy?.Primitives?.["main-limit-order"]?.["Filled-Percentage"] || "0"
    output = agentInfo?.Strategy?.["Min-Output"] || "0"
  } else if (order.type === "stop-order") {
    filledPrice = "n/A"
    filledPercentage = "n/A"
    stopVariant =
      agentInfo?.Strategy?.["Is-Stop-Limit"] === "true"
        ? "limit"
        : agentInfo?.Strategy?.["Is-Trailing"] === "true"
          ? "trailing"
          : "market"
    stopTriggerPrice = agentInfo?.Strategy?.["Stop-Price"] || "0"

    output = agentInfo?.Strategy?.["Stop-Limit-Min-Output"] || "n/A"
    //|| (BigInt(order.input) * BigInt(stopTriggerPrice)) / 10n ** 18n

    if (stopVariant === "trailing") {
      trailingDiff = agentInfo?.Strategy?.["Trailing-Difference"]
      stopTriggerPrice = agentInfo?.Strategy?.Primitives?.["stop-trigger"]?.["Stop-Price"] || "0"
    }
    if (stopVariant === "limit") {
      stopLimitPrice = (BigInt(output) / BigInt(order.input)) * 10n ** 18n
      triggerPriceHit = agentInfo?.Strategy?.Primitives?.["stop-trigger"]?.["Was-Hit"] === "true"
      filledPercentage =
        agentInfo?.Strategy?.Primitives?.["stop-limit-order"]?.["Filled-Percentage"] || "n/A"
      filledPrice = agentInfo?.Strategy?.Primitives?.["stop-limit-order"]?.["Filled-Price"] || "n/A"
    }
  } else {
    filledPrice = agentInfo?.Strategy?.["Filled-Price"]
    filledPercentage = agentInfo?.Strategy?.["Filled-Percentage"] || "0"
    output = agentInfo?.Strategy?.["Accumulated-Output"] || "0"
  }

  if (filledPrice === "n/A") filledPrice = null
  if (filledPercentage === "n/A") filledPercentage = "0"

  const entityId = order.id

  const [tokenPrices] = useTokenPrices()

  const { data: tokenFromBalance } = useQuery({
    queryKey: ["balance", entityId, order.token_from],
    queryFn: async () => {
      if (!entityId) throw new Error("Invalid params")
      const data = await getBalance(order.token_from, entityId)
      console.log("onTokenBalance", order.token_from, data)
      return data
    },
  })

  const { data: tokenToBalance } = useQuery({
    queryKey: ["balance", entityId, order.token_to],
    queryFn: async () => {
      if (!entityId) throw new Error("Invalid params")
      const data = await getBalance(order.token_to, entityId)
      console.log("onTokenBalance", order.token_to, data)
      return data
    },
  })

  const [fromToken] = useTokenInfo(order.token_from)
  const [toToken] = useTokenInfo(order.token_to)

  const networth = useMemo(() => {
    if (
      !tokenPrices ||
      tokenFromBalance === undefined ||
      tokenToBalance === undefined ||
      !fromToken ||
      !toToken
    )
      return null
    const fromPrice = tokenPrices[fromToken.id]?.price || 0
    const toPrice = tokenPrices[toToken.id]?.price || 0

    const agentNetworth =
      fromPrice * parseFloat(parseBigIntAsNumber(tokenFromBalance, fromToken.denomination)) +
      toPrice * parseFloat(parseBigIntAsNumber(tokenToBalance, toToken.denomination))

    onAgentValue?.(agentNetworth)
    return agentNetworth
  }, [tokenPrices, tokenFromBalance, tokenToBalance, fromToken, toToken])

  const { data: transactions } = useQuery({
    queryKey: ["agent-txns", order.id],
    queryFn: () => getAgentTxns(order.id),
  })

  const { data: lastTxns } = useQuery({
    queryKey: ["agent-last-txns", order.id],
    queryFn: async () => {
      const txns = await getLastTransactions(order.id)
      return txns
    },
  })

  const vol24h = useMemo(() => {
    if (!lastTxns || !fromToken || !toToken || !tokenPrices || !tokenPrices[fromToken.id])
      return null

    const volume = lastTxns.reduce((acc, txn) => {
      return acc + BigInt(txn.tags["From-Quantity"])
    }, 0n)

    const volumeUsd =
      tokenPrices[fromToken.id].price *
      parseFloat(parseBigIntAsNumber(volume, fromToken.denomination))

    return volumeUsd
  }, [lastTxns, fromToken, toToken])

  if (singleAgentView) {
    return (
      <Stack gap={2}>
        <Stack gap={4} direction="row" flexWrap={"wrap"}>
          {order.input && (
            <Stack>
              <Typography variant="caption" color="secondary">
                Input
              </Typography>
              <TokenAmountBlock
                amount={order.input}
                tokenId={order.token_from}
                hideCopyToClipboard
                needsParsing
                showTicker
                color="accent.main"
              />
            </Stack>
          )}
          {output && (
            <Stack>
              <Typography variant="caption" color="secondary">
                Output
              </Typography>
              <TokenAmountBlock
                amount={output}
                tokenId={order.token_to}
                hideCopyToClipboard
                needsParsing
                showTicker
                color="accent.main"
              />
            </Stack>
          )}

          {order.type !== "stop-order" && fromToken && toToken && (
            <>
              <Stack>
                <Typography variant="caption" color="secondary">
                  Filled
                </Typography>
                <Typography variant="inherit" color="accent.main" fontFamily={NumberFont}>
                  {filledPercentage}/100
                </Typography>
              </Stack>

              <Stack>
                <Typography variant="caption" color="secondary">
                  Avg. Fill Price
                </Typography>
                {!filledPrice ? (
                  <Typography variant="inherit" color="accent.main">
                    N/A
                  </Typography>
                ) : (
                  <TokenAmountBlock
                    amount={parseBigIntAsNumber(
                      filledPrice,
                      PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                    )}
                    tokenId={toToken.id}
                    hideCopyToClipboard
                    color="accent.main"
                  />
                )}
              </Stack>
            </>
          )}

          {order.type === "stop-order" &&
            stopTriggerPrice !== undefined &&
            fromToken &&
            toToken && (
              <Stack>
                <Typography variant="caption" color="secondary">
                  Trigger
                </Typography>
                <Typography
                  color="accent.main"
                  fontSize="inherit"
                  fontFamily={NumberFont}
                  fontWeight={500}
                  variant="inherit"
                >
                  {formatNumberAuto(
                    parseBigIntAsNumber(
                      stopTriggerPrice,
                      PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                    ),
                  )}
                </Typography>
                {stopVariant === "limit" && triggerPriceHit && (
                  <Check
                    height={15}
                    width={15}
                    style={{
                      marginTop: 2,
                      color: "var(--mui-palette-success-main)",
                    }}
                  />
                )}
              </Stack>
            )}

          {order.type === "stop-order" &&
            stopVariant === "limit" &&
            stopLimitPrice !== undefined &&
            fromToken &&
            toToken && (
              <Stack>
                <Typography variant="caption" color="secondary">
                  Limit
                </Typography>
                <Typography
                  color="accent.main"
                  fontSize="inherit"
                  fontFamily={NumberFont}
                  fontWeight={500}
                  variant="inherit"
                >
                  {formatNumberAuto(
                    parseBigIntAsNumber(
                      stopLimitPrice,
                      PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                    ),
                  )}
                </Typography>
              </Stack>
            )}

          {order.type === "stop-order" &&
            stopVariant === "trailing" &&
            trailingDiff !== undefined && (
              <Stack>
                <Typography variant="caption" color="secondary">
                  Trailing
                </Typography>
                <Typography fontSize="inherit" fontFamily={NumberFont} fontWeight={500}>
                  {Number(trailingDiff) / 100}%
                </Typography>
              </Stack>
            )}

          {order.type === "stop-order" && stopVariant && (
            <Stack>
              <Typography variant="caption" color="secondary">
                Stop Variant
              </Typography>
              <Typography color="secondary">{stopVariant}</Typography>
            </Stack>
          )}

          <Stack>
            <Typography variant="caption" color="secondary">
              Created
            </Typography>
            <Typography variant="inherit" color="secondary">
              {formatRelative(order.created_at_ts)}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="secondary">
              Expires at
            </Typography>
            <Typography variant="inherit" color="secondary">
              {formatFullDate(order.expires_at_ts)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    )
  }

  return (
    <>
      <TableRow
        style={{
          background: !showDetails
            ? "var(--mui-palette-paperAlt)"
            : "var(--mui-palette-paperAltSelected)",
        }}
        sx={{
          transition: "background 0.3s",
          fontSize: "1rem",
          fontWeight: 700,
          "& > td": { border: 0, paddingTop: 2 },
        }}
      >
        <TableCell align="right">{index + 1}</TableCell>
        <TableCell>
          <Stack>
            <Typography variant="body1" fontWeight={700}>
              {TITLE_MAP[order.type]}
            </Typography>
            <Typography variant="caption">
              <IdBlock
                color="text.secondary"
                label={truncateId(order.id)}
                value={order.id}
                href={`/entity/${order.id}`}
              />
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="right">
          {transactions !== undefined ? (
            <Typography variant="body1" fontWeight={700}>
              {transactions}
            </Typography>
          ) : (
            <Skeleton width={40} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
        <TableCell align="right">
          {typeof vol24h === "number" ? (
            <Typography
              fontFamily={NumberFont}
              fontWeight={700}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              <UsdAmountBlock amount={vol24h} />
            </Typography>
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
        <TableCell align="right">
          {networth !== null ? (
            <Typography
              fontFamily={NumberFont}
              fontWeight={700}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              <UsdAmountBlock amount={networth} />
            </Typography>
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
      </TableRow>

      <TableRow
        sx={{
          fontSize: "1rem",
          fontWeight: 700,
          "&:last-of-type > td": {
            borderBottomRightRadius: 15,
            borderBottomLeftRadius: 15,
          },
        }}
      >
        <TableCell
          colSpan={9}
          style={{
            background: !showDetails
              ? "var(--mui-palette-paperAlt)"
              : "var(--mui-palette-paperAltSelected",
          }}
          sx={{
            background: "var(--mui-palette-paperAlt)",
            transition: "background 0.3s",
          }}
        >
          <Stack alignItems="center" marginBottom={1}>
            <Button
              onClick={() => {
                setShowDetails(!showDetails)
              }}
              size="small"
              sx={{
                background: (theme) => theme.palette.action.hover,
                minWidth: "unset",
                width: 50,
                height: 16,
              }}
            >
              {showDetails ? (
                <KeyboardArrowUp fontSize="small" />
              ) : (
                <KeyboardArrowDown fontSize="small" />
              )}
            </Button>
          </Stack>
          <Collapse in={showDetails}>
            <Box
              sx={{
                overflow: "hidden",
                borderRadius: 1,
                background: "var(--mui-palette-paperAlt)",
                marginBottom: 1,
                paddingX: 3,
                paddingY: 2,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <Stack gap={1} alignItems="flex-start">
                {order.input && (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="inherit" color="secondary">
                      Input:
                    </Typography>
                    <TokenAmountBlock
                      amount={order.input}
                      tokenId={order.token_from}
                      hideCopyToClipboard
                      needsParsing
                      showTicker
                      color="accent.main"
                    />
                  </Stack>
                )}
                {output && (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="inherit" color="secondary">
                      Output:
                    </Typography>
                    <TokenAmountBlock
                      amount={output}
                      tokenId={order.token_to}
                      hideCopyToClipboard
                      needsParsing
                      showTicker
                      color="accent.main"
                    />
                  </Stack>
                )}

                {order.type !== "stop-order" && (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="inherit" color="secondary">
                      Filled:
                    </Typography>
                    <Typography variant="inherit" color="accent.main" fontFamily={NumberFont}>
                      {filledPercentage}/100
                    </Typography>
                  </Stack>
                )}

                {order.type !== "stop-order" && fromToken && toToken && (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="inherit" color="secondary">
                      Avg. Fill Price:
                    </Typography>
                    {!filledPrice ? (
                      <Typography variant="inherit" color="accent.main">
                        N/A
                      </Typography>
                    ) : (
                      <TokenAmountBlock
                        amount={parseBigIntAsNumber(
                          filledPrice,
                          PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                        )}
                        tokenId={toToken.id}
                        hideCopyToClipboard
                        color="accent.main"
                      />
                    )}
                  </Stack>
                )}

                {order.type === "stop-order" &&
                  stopTriggerPrice !== undefined &&
                  fromToken &&
                  toToken && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography variant="inherit" color="secondary">
                        Trigger:
                      </Typography>
                      <Typography
                        color="accent.main"
                        fontSize="inherit"
                        fontFamily={NumberFont}
                        fontWeight={500}
                        variant="inherit"
                        sx={{ textOverflow: "ellipsis", overflow: "hidden" }}
                      >
                        {formatNumberAuto(
                          parseBigIntAsNumber(
                            stopTriggerPrice,
                            PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                          ),
                        )}
                      </Typography>
                      {stopVariant === "limit" && triggerPriceHit && (
                        <Check
                          height={15}
                          width={15}
                          style={{
                            marginTop: 2,
                            color: "var(--mui-palette-success-main)",
                          }}
                        />
                      )}
                    </Stack>
                  )}

                {order.type === "stop-order" &&
                  stopVariant === "limit" &&
                  stopLimitPrice !== undefined &&
                  fromToken &&
                  toToken && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography variant="inherit" color="secondary">
                        Limit:
                      </Typography>
                      <Typography
                        color="accent.main"
                        fontSize="inherit"
                        fontFamily={NumberFont}
                        fontWeight={500}
                        variant="inherit"
                        sx={{ textOverflow: "ellipsis", overflow: "hidden" }}
                      >
                        {formatNumberAuto(
                          parseBigIntAsNumber(
                            stopLimitPrice,
                            PRICE_DECIMALS - fromToken.denomination + toToken.denomination,
                          ),
                        )}
                      </Typography>
                    </Stack>
                  )}

                {order.type === "stop-order" &&
                  stopVariant === "trailing" &&
                  trailingDiff !== undefined && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography variant="inherit" color="secondary">
                        Trailing:
                      </Typography>
                      <Typography
                        fontSize="inherit"
                        fontFamily={NumberFont}
                        fontWeight={500}
                        component={"div"}
                        sx={{
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {Number(trailingDiff) / 100}%
                      </Typography>
                    </Stack>
                  )}

                {order.type === "stop-order" && stopVariant && (
                  <Typography variant="inherit" color="secondary">
                    Stop Variant: {stopVariant}
                  </Typography>
                )}
              </Stack>

              <Stack gap={1}>
                <Typography variant="inherit" color="secondary">
                  Created: {formatRelative(order.created_at_ts)}
                </Typography>
                <Typography variant="inherit" color="secondary">
                  Expires at: {formatFullDate(order.expires_at_ts)}
                </Typography>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export const OrderRow = memo(OrderRowBase)
